import React from 'react';
import { Box, Typography, Avatar, IconButton, Chip } from '@mui/material';
import { colors } from '../../styled/StyledComponents';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import { styled } from '@mui/material/styles';

// Styled components
const CardWrapper = styled(Box)(({ theme, index }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
  padding: theme.spacing(3),
  borderRadius: '16px',
  background: colors.background,
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '6px',
    height: '100%',
    background: [colors.primary, colors.secondary, colors.tertiary, colors.primary][index % 4],
    borderTopLeftRadius: '16px',
    borderBottomLeftRadius: '16px',
  },
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
    '& .member-avatar': {
      transform: 'scale(1.05)',
      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
    },
    '& .social-icons': {
      opacity: 1,
      transform: 'translateY(0)',
    },
    '& .member-role': {
      color: [colors.primary, colors.secondary, colors.tertiary, colors.primary][index % 4],
    },
  },
}));

const MemberAvatar = styled(Avatar)(({ theme }) => ({
  width: 90,
  height: 90,
  border: '3px solid white',
  boxShadow: '0 8px 20px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.4s ease',
}));

const SocialIcons = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(2),
  right: theme.spacing(2),
  display: 'flex',
  gap: theme.spacing(1),
  opacity: 0,
  transform: 'translateY(10px)',
  transition: 'all 0.3s ease',
}));

const SocialIconButton = styled(IconButton)(({ theme, colorindex }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  color: [colors.primary, colors.secondary, colors.tertiary, colors.primary][colorindex % 4],
  padding: theme.spacing(1),
  '&:hover': {
    backgroundColor: [colors.primary, colors.secondary, colors.tertiary, colors.primary][colorindex % 4],
    color: 'white',
    transform: 'translateY(-3px)',
  },
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.05)',
}));

const SpecialtyChip = styled(Chip)(({ theme, colorindex }) => ({
  backgroundColor: [
    `${colors.primary}15`, 
    `${colors.secondary}15`, 
    `${colors.tertiary}15`, 
    `${colors.primary}15`
  ][colorindex % 4],
  color: [colors.primary, colors.secondary, colors.tertiary, colors.primary][colorindex % 4],
  fontWeight: 500,
  fontSize: '0.75rem',
  height: 24,
  marginRight: theme.spacing(1),
  marginBottom: theme.spacing(1),
  '& .MuiChip-label': {
    padding: '0 8px',
  },
}));

const TeamMemberCard = ({ member, onClick, index = 0 }) => {
  return (
    <CardWrapper onClick={onClick} index={index}>
      <MemberAvatar
        src={member.image}
        alt={member.name}
        className="member-avatar"
      />
      <Box sx={{ flex: 1 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 700,
            mb: 0.5,
            color: colors.text,
          }}
        >
          {member.name}
        </Typography>
        <Typography 
          variant="body2" 
          className="member-role"
          sx={{ 
            color: colors.textLight,
            fontWeight: 500,
            mb: 1,
            transition: 'color 0.3s ease',
          }}
        >
          {member.role}
        </Typography>
        
        {member.specialties && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 1 }}>
            {member.specialties.map((specialty, i) => (
              <SpecialtyChip
                key={i}
                label={specialty}
                size="small"
                colorindex={index}
              />
            ))}
          </Box>
        )}
      </Box>
      
      <SocialIcons className="social-icons">
        {member.linkedin && (
          <SocialIconButton 
            size="small" 
            aria-label="LinkedIn profile"
            colorindex={index}
          >
            <LinkedInIcon fontSize="small" />
          </SocialIconButton>
        )}
        {member.twitter && (
          <SocialIconButton 
            size="small" 
            aria-label="Twitter profile"
            colorindex={index}
          >
            <TwitterIcon fontSize="small" />
          </SocialIconButton>
        )}
      </SocialIcons>
    </CardWrapper>
  );
};

export default TeamMemberCard;
